import * as React from "react";
import { X } from "lucide-react";

import { cn } from "@/utils/cn";

/**
 * docs/ui/design-system.md §11 Chip: filter (toggleable), input (removable),
 * or display-only — one 32px-height size across all three. Not a shadcn
 * registry item; BazaarX's Chip (toggle + remove semantics together) doesn't
 * map onto shadcn's Badge or Toggle individually.
 */
export interface ChipProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  label: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  isRemovable?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
}

export function Chip({
  label,
  isSelected = false,
  isDisabled = false,
  isRemovable = false,
  onRemove,
  onClick,
  className,
  ...props
}: ChipProps) {
  const isInteractive = Boolean(onClick) && !isDisabled;

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-pressed={isInteractive ? isSelected : undefined}
      aria-disabled={isDisabled || undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={
        isInteractive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-body-sm transition-colors",
        isSelected
          ? "border-transparent bg-primary text-primary-foreground"
          : "border-input bg-background text-foreground",
        isInteractive && !isSelected && "hover:bg-muted",
        isInteractive &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        isDisabled && "pointer-events-none opacity-50",
        className,
      )}
      {...props}
    >
      <span>{label}</span>
      {isRemovable && (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          disabled={isDisabled}
          onClick={(event) => {
            event.stopPropagation();
            onRemove?.();
          }}
          className="-mr-1 rounded-full p-0.5 hover:bg-black/10"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
